export enum RecipeCategoryTypes {
  Runtimes = 'runtimes',
  Backend = 'backend',
  Frontend = 'frontend',
  ProjectUtility = 'project-utility',
  ServiceUtility = 'service-utility',
  PlatformBots = 'platform-bots',
  Other = 'other'
}

export interface StrapiMedia {
  id: number;
  documentId: string;
  name: string;
  alternativeText: string | null;
  caption: string | null;
  width: number;
  height: number;
  formats: unknown;
  hash: string;
  ext: string;
  mime: string;
  size: number;
  url: string;
  previewUrl: string | null;
  provider: string;
  provider_metadata: unknown;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
}

export interface RecipeOld {
  id: string;
  slug?: string;
  recipeId?: string;
  enableOnboarding?: boolean;
  tag?: string;
  logo?: string;
  icon?: string;
  category?: RecipeCategoryTypes;
  importYaml: string;
  desc?: string;
  disabled?: boolean;
  listed: boolean;
  title?: string;
  content?: string;
  repository?: {
    name?: string;
    repository: string;
  }[];
}

export interface RecipeServiceResource {
  cpuCoreCount: number;
  memoryGBytes: number;
  diskGBytes: number;
}

export interface RecipeServiceFreeResource extends RecipeServiceResource {
  cpuCorePercent: number;
  memoryPercent: number;
}

export interface RecipeServiceVerticalAutoscaling {
  maxResource: RecipeServiceResource;
  minResource: RecipeServiceResource;
  minFreeResource: RecipeServiceFreeResource;
  cpuMode: 'SHARED' | 'DEDICATED';
  startCpuCoreCount: number;
  swapEnabled: boolean;
}

export interface RecipeServiceAutoscaling {
  verticalAutoscaling: RecipeServiceVerticalAutoscaling;
  horizontalAutoscaling: {
    maxContainerCount: number;
    minContainerCount: number;
  };
}

export interface RecipeServicePort {
  protocol: string;
  port: number;
  description: string;
  scheme: string;
}

// Service category from backend
export type RecipeServiceCategory = 'USER' | 'STANDARD' | 'OBJECT_STORAGE' | 'LOCAL_STORAGE' | 'CORE';

export interface RecipeService {
  name: string;
  typeId: string;                    // e.g., "php_nginx", "valkey"
  typeName: string;                  // e.g., "PHP+Nginx", "Valkey"
  typeVersionId: string;             // e.g., "php_v8_4_nginx_v1_22"
  typeVersionName: string;           // e.g., "php-nginx@8.4"
  exactVersionNumber: string;        // e.g., "v8.4.1"
  category: RecipeServiceCategory;
  extracts?: Record<string, string>;
  gitRepo: string;
  content: string;
  zeropsYaml: string;
  autoscaling?: RecipeServiceAutoscaling; // Optional - object storage doesn't have this
  objectStorageSize?: number; // GB - only for object storage services
  mode: 'HA' | 'NON_HA';
  ports: RecipeServicePort[];
}

export interface RecipeEnvironment {
  name: string;
  content: string;
  extracts?: Record<string, string>;
  import: string;
  projectName: string;
  services: RecipeService[];
}

// Extended environment with parsed name fields
export interface RecipeEnvironmentParsed extends RecipeEnvironment {
  _order: number;
  key: string;
  name: string;  // Display name extracted from environment name
}

export interface RecipeSourceData {
  name: string;
  content: string;
  extracts?: Record<string, string>;
  environments: RecipeEnvironment[];
}

export interface Recipe {
  id: number;
  documentId: string;
  name: string;
  icon: string;
  iconImage?: StrapiMedia[];
  iconImageDark?: StrapiMedia[];
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  slug: string;
  internalType?: string;
  // Ownership model (native Strapi column) driving the detail-page "Get started"
  // flow. Orthogonal to internalType; nullable — unset falls back to legacy
  // detection. 'app' = fork-and-own, 'software' = operate prebuilt OSS.
  shape?: 'app' | 'software';
  source?: string;
  sourceData: RecipeSourceData;
  externalDataCached?: boolean;
  externalDataFetchedAt?: string;
  recipeCategories?: RecipeCategory[];
  recipeLanguageFrameworks?: RecipeLanguageFramework[];
}

export interface RecipeCategory {
  id: number;
  documentId: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  slug: string;
}

export interface RecipeLanguageFramework {
  id: number;
  documentId: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  icon: string;
  iconImage?: StrapiMedia;
  iconImageDark?: StrapiMedia;
  slug: string;
}

// Extended interface for efficient access
export interface RecipeExtended extends Omit<Recipe, 'sourceData'> {
  // Map for O(1) environment lookup by key (name without order prefix)
  _environmentsMap: Record<string, RecipeEnvironmentParsed>;
  // Override sourceData to have parsed environments
  sourceData?: {
    name: string;
    content: string;
    extracts?: Record<string, string>;
    environments: RecipeEnvironmentParsed[];
  };
}

// Preferred environment keys in order of priority (production environments first)
const PREFERRED_ENV_KEYS = [ 'small-production', 'production', 'highly-available-production' ];

/**
 * Get the default environment from a sorted array of environments.
 * Prefers production environments (small-production, production, highly-available-production)
 * Falls back to middle environment if no preferred environment exists.
 */
export function getDefaultEnvironment<T extends { key: string }>(
  sortedEnvArray: T[]
): T | undefined {
  if (!sortedEnvArray?.length) {
    return undefined;
  }

  // Try to find a preferred environment
  for (const preferredKey of PREFERRED_ENV_KEYS) {
    const found = sortedEnvArray.find((env) => env.key === preferredKey);
    if (found) {
      return found;
    }
  }

  // Fallback to middle environment
  return sortedEnvArray[Math.floor((sortedEnvArray.length - 1) / 2)];
}

// GitHub Recipe Models
//
// NEW /api/recipe/info shape (post `.zerops-recipe` loader rollout). Mirrors
// RecipeSourceData / RecipeEnvironment / RecipeService. Legacy flat-root repos
// now return `environments: []` (deprecated, but not an error).
export interface GitHubRecipeApiResponse {
  name: string;                          // repo/recipe name
  content: string;                       // recipe-root README, raw markdown
  extracts?: Record<string, string>;     // recipe-level, already sliced server-side
  environments: RecipeEnvironment[];     // one per env folder; [] for legacy flat repos
  errors: unknown | null;
}

export interface GitHubRecipeYaml {
  name: string;        // Environment slug/key (e.g. "small-production")
  content: string;     // Import YAML content (verbatim env.import)
  url: string;         // URL to file in repository (best-effort, may be '')
  environment?: string; // Environment key/slug, e.g. "small-production"
  isDefault?: boolean; // True for the resolved default environment
  displayName?: string; // Raw env name, e.g. "4 — Small Production"
  type: 'import' | 'other'; // Always 'import' for the new env-based shape
}

export interface GitHubRecipeRepository {
  owner: string;       // "zeropsio"
  repo: string;        // "recipe-laravel"
  url: string;         // "https://github.com/zeropsio/recipe-laravel"
  branch?: string;     // "main"
}

// Per-environment data parsed from the new /api/recipe/info response, carried
// additively on GitHubRecipe so the read-path can compose rich detail without
// re-fetching. Shape parallels RecipeEnvironmentParsed.
export interface GitHubRecipeEnvironment {
  key: string;                       // slug, e.g. "small-production"
  order: number;                     // parsed leading order, 999 if absent
  name: string;                      // display name, e.g. "Small Production"
  displayName: string;               // raw name, e.g. "4 — Small Production"
  content: string;                   // per-env README raw
  extracts?: Record<string, string>; // per-env sliced extracts
  import: string;                    // full import yaml, verbatim
  projectName: string;
  services: RecipeService[];
}

export interface GitHubRecipe {
  id: string;          // Generated from URL
  sourceUrl: string;   // URL as requested (may carry "@branch"), the lookup key
  repository: GitHubRecipeRepository;
  readmeMd?: string;   // README markdown content from API (recipe-root README)
  yamls: GitHubRecipeYaml[];
  defaultYaml?: GitHubRecipeYaml; // resolved default environment YAML (undefined for empty responses)
  environments: string[];        // parsed environment keys, e.g. ["small-production"]
  selectedYaml?: string; // Track URL-specified YAML
  // -- additive rich data from the new shape (read-path composition)
  name?: string;                          // recipe/repo name from response
  extracts?: Record<string, string>;      // recipe-level extracts (already sliced)
  parsedEnvironments?: GitHubRecipeEnvironment[]; // parsed per-env detail
}

export interface NonAuthRecipeSelection {
  type: 'strapi' | 'github';
  recipeSlug: string;        // For Strapi: recipe slug, For GitHub: base64(url)
  environmentName: string;   // Environment/YAML variant
  githubUrl?: string;        // Only for GitHub recipes
  recipeSourceUrl?: string;  // Only for Strapi recipes - full recipe URL with environment
}

/**
 * Response from GET /api/recipes/:id/related
 */
export interface RelatedRecipesResponse {
  data: Recipe[];
  meta: {
    total: number;
    limit: number;
    currentRecipeId: number;
  };
}
